import React, { useEffect, useState, Fragment } from 'react'

// third party components
import { Dialog, Transition } from '@headlessui/react'

// web3 part
import { useMetaplex } from 'hooks/useMetaplex'
import { useLocalWalletBundlr } from 'hooks/useLocalWalletBundlr'
import { PublicKey } from '@solana/web3.js'
import { UpdateNftInput, toMetaplexFileFromBrowser } from '@metaplex-foundation/js'

// next components
import Input from 'components/Input'

// custom components
import { limit } from 'utils/strings'
import Textarea from 'components/Input/Textarea'

// resources
import CloseIcon from 'assets/icons/close-white-black-15x15.svg?component'
import EditorGreenIcon from 'assets/icons/editor-white.svg?component'

// styles
import styles from './index.module.scss'
import GradientButton from 'components/Button/GradientButton'
import Dropzone from 'react-dropzone'
import LoadingButton from 'components/Button/LoadingButton'

// apis
import collectionsAPI from 'apis/collections'
import ImageWithFoster from 'components/ImageWithFoster'
import Alert from 'components/Alert'

const CollectionDetailModal = props => {
  const { open, close, data, onSuccess } = props
  const { metaplex } = useMetaplex()
  const { storage } = useLocalWalletBundlr()
  const [artName, setArtName] = useState(data.collection_name)
  const [artDesc, setArtDesc] = useState(data.description)
  const [selectedFile, setSelectedFile] = useState<any>(null)
  const [imageUrl, setImageUrl] = useState(data.cover_image)
  const [loading, setLoading] = useState(false)
  const [enableSave, setEnableSave] = useState(false)
  const [showAlert, setShowAlert] = useState<boolean>(false)
  const [alertData, setAlertData] = useState<any[]>([])

  useEffect(() => {
    setAlertData([
      {
        id: 0,
        title: 'Collection Metadata Updated',
        description: 'Collection is updated successfully!',
        type: 'success',
      },
    ])
  }, [])

  useEffect(() => {
    if (selectedFile !== null && selectedFile !== undefined) {
      setImageUrl(URL.createObjectURL(selectedFile[0]))
    }
  }, [selectedFile])

  const closeModal = () => {
    close()
  }

  const checkEnableSave = () => {
    if (selectedFile != null) {
      setEnableSave(true)
      return
    }

    if (artName !== data.collection_name) {
      setEnableSave(true)
      return
    }
    if (artDesc !== data.description) {
      setEnableSave(true)
      return
    }
    setEnableSave(false)
    return
  }

  useEffect(() => {
    checkEnableSave()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [artName, artDesc, selectedFile])

  const updateCollectionData = async () => {
    // web3 part
    setLoading(true)

    let chainNft = await metaplex.nfts().findByMint({
      mintAddress: new PublicKey(data.collection_id),
    })

    let needsJsonUpload = false
    let oldJson = chainNft.json ?? {}
    let newJson = structuredClone(oldJson)
    newJson.properties ??= {}
    newJson.properties.files ??= []

    if (artName != oldJson.name) {
      newJson.name = artName
      needsJsonUpload = true
    }
    if (artDesc != oldJson.description) {
      newJson.description = artDesc
      needsJsonUpload = true
    }

    let updateInput: UpdateNftInput = { nftOrSft: chainNft }
    if (artName != chainNft.name) updateInput.name = artName
    let newImageUrl = ''
    if (selectedFile !== null) {
      let metaplexFile = await toMetaplexFileFromBrowser(/* nft image file */ selectedFile[0])
      newImageUrl = await storage.upload(metaplexFile)
      for (let file of newJson.properties.files)
        if (file.url == newJson.image) {
          file.url = newImageUrl
          file.type = metaplexFile.contentType
        }
      if (newJson.properties.files.length == 0)
        newJson.properties.files.push({
          uri: newImageUrl,
          type: metaplexFile.contentType,
        })
      newJson.image = newImageUrl
      needsJsonUpload = true
    }

    if (needsJsonUpload) updateInput.uri = await storage.uploadJson(newJson)

    if (Object.keys(updateInput).length > 1) {
      try {
        await metaplex.nfts().update(updateInput)

        let params = {
          collectionName: artName,
          description: artDesc,
        }

        if (newImageUrl.length > 0) {
          params['coverImage'] = newImageUrl
        }

        await collectionsAPI.updateCollection(data.collection_id, params).then(
          response => {
            setLoading(false)
            setEnableSave(false)
            setShowAlert(true)
            setTimeout(() => {
              onSuccess(response)
            }, 3000)
          },
          error => {
            console.log(error)
            setLoading(false)
          }
        )
      } catch {
        setLoading(false)
      }
    } else {
      close('cancel')
      setLoading(false)
    }
  }

  return (
    <div>
      <Transition show={open} as={Fragment}>
        <Dialog as="div" className={styles.container} onClose={() => closeModal()}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className={styles.bgView} />
          </Transition.Child>

          <div className="fixed inset-0 overflow-y-hidden">
            <div className="flex min-h-full items-center justify-center text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 scale-95"
                enterTo="opacity-100 scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 scale-100"
                leaveTo="opacity-0 scale-95"
              >
                <Dialog.Panel className={styles.modalView}>
                  <div className={styles.close} onClick={() => closeModal()}>
                    <CloseIcon alt="" />
                  </div>
                  <div className={styles.editProfile}>Edit Profile</div>

                  <div className="w-full h-[calc(100vh-220px)] sm:h-[calc(100vh-470px)] mt-[30px] overflow-auto pr-[16px] sm:pr-[45px]">
                    <div className={styles.artImage}>
                      <ImageWithFoster src={imageUrl} alt="" className="w-full h-full object-cover" />
                      <div className="absolute bottom-[10px] right-[10px] z-[1000]">
                        <Dropzone
                          onDrop={acceptedFiles => {
                            acceptedFiles.length !== 0 && setSelectedFile(acceptedFiles)
                          }}
                          onDropRejected={rejectedFiles => {}}
                          maxFiles={1}
                          accept={{ 'image/jpeg': [], 'image/png': [], 'image/gif': [] }}
                          maxSize={31457280}
                        >
                          {({ getRootProps, getInputProps, open }) => (
                            <section>
                              <button onClick={open} style={{ display: 'none' }}>
                                open
                              </button>
                              <div {...getRootProps()}>
                                <input {...getInputProps()} />
                                <div className={styles.editorIcon}>
                                  <EditorGreenIcon />
                                </div>
                              </div>
                            </section>
                          )}
                        </Dropzone>
                      </div>
                    </div>
                    <div className={`${styles.artName} mt-[30px]`}>Collection Name</div>
                    <div className="w-full mt-[15px]">
                      <Input
                        value={limit(artName, 32)}
                        placeholder="Give your collection a name"
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="23px"
                        label01="Max Characters 32"
                        label02={32 - artName.length === 32 ? '' : `${32 - artName.length} Characters left`}
                        onChange={value => setArtName(limit(value, 32))}
                      />
                    </div>
                    <div className={`${styles.artName} mt-[30px]`}>Collection Description</div>
                    <div className="w-full mt-[15px]">
                      <Textarea
                        value={limit(artDesc, 1000)}
                        color="var(--text-gray-200-color)"
                        backgroundColor="var(--bg-101010-F3F2F0)"
                        borderColor="rgba(var(--main-bg-color-rgb), 0.1)"
                        borderRadius="16px"
                        height="142px"
                        placeholder="Add a short description"
                        label01="Max Characters 1000"
                        label02={1000 - artDesc.length === 1000 ? '' : `${1000 - artDesc.length} Characters left`}
                        onChange={value => setArtDesc(limit(value, 1000))}
                      />
                    </div>
                  </div>

                  <div className="mt-[30px] lg:mt-[30px] w-full flex justify-center lg:justify-end items-center gap-6 pr-[16px] sm:pr-[45px]">
                    {loading ? (
                      <LoadingButton fullWidth />
                    ) : (
                      <GradientButton
                        fullWidth
                        label="Save"
                        disabled={!enableSave}
                        onClick={() => {
                          updateCollectionData()
                        }}
                      />
                    )}
                  </div>

                  <Alert
                    open={showAlert}
                    close={() => {
                      setShowAlert(false)
                    }}
                    data={alertData}
                  />
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition>
    </div>
  )
}

export default CollectionDetailModal
